import { FC, useMemo } from "react";
import styled from "@emotion/styled";
import {
  MDSButton,
  MDSButtonGroup,
  MDSButtonVariant,
  MDSDialogSlotName,
  MDSDialogTextContent,
  MDSModalDialog,
  MDSText,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import {
  ConfigurationList,
  ConfigurationListItem,
  ConfigurationListItemKind,
  ConfigurationPermissionStatus,
} from "@/components/configuration";
import { RecallPermissionKind } from "@/modules/electron/types";
import { ItemListSectionHeaderRow } from "@/components/item-list/rows/variants/item-list-section-header-row/item-list-section-header-row";
import { ItemListRowType } from "@/components/item-list/types";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  resolveConfigurationStatus: ({
    kind,
  }: {
    kind: RecallPermissionKind;
  }) => ConfigurationPermissionStatus;
  handleEnablePermission: ({ kind }: { kind: RecallPermissionKind }) => Promise<void>;
  handleOpenPermissionPane: ({ kind }: { kind: RecallPermissionKind }) => Promise<void>;
  advancedMeetingPermissionsRequired: boolean;
  restartRequired: boolean;
  onRestart: () => void;
  isDetectionOfUnscheduledMeetingsEnabled: boolean;
  onChangeDetectionOfUnscheduledMeetings: (checked: boolean) => void;
  isRemindMeToJoinMeetingsAndTakeNotesEnabled: boolean;
  onChangeRemindMeToJoinMeetingsAndTakeNotes: (checked: boolean) => void;
  platform?: {
    isMacOs?: boolean;
    isIntelMacDesktop?: boolean;
  };
};

export const MeetingPermissionsModalContent: FC<Props> = ({
  isOpen,
  onClose,
  resolveConfigurationStatus,
  handleEnablePermission,
  handleOpenPermissionPane,
  onRestart,
  restartRequired,
  advancedMeetingPermissionsRequired,
  isDetectionOfUnscheduledMeetingsEnabled,
  onChangeDetectionOfUnscheduledMeetings,
  isRemindMeToJoinMeetingsAndTakeNotesEnabled,
  onChangeRemindMeToJoinMeetingsAndTakeNotes,
  platform,
}) => {
  const isMacOs = platform?.isMacOs ?? true;
  const isIntelMacDesktop = platform?.isIntelMacDesktop ?? false;
  const accessibilityPermissionStatus = resolveConfigurationStatus({
    kind: RecallPermissionKind.Accessibility,
  });

  const items = useMemo<ConfigurationListItem[]>(() => {
    if (isMacOs && isIntelMacDesktop) return [];

    const itemList: ConfigurationListItem[] = [
      {
        kind: ConfigurationListItemKind.Toggle,
        id: "detect-unscheduled-meetings",
        title: "Detect unscheduled meetings and remind me to have Mem take notes",
        checked: isDetectionOfUnscheduledMeetingsEnabled,
        onChange: onChangeDetectionOfUnscheduledMeetings,
      },
    ];

    // Windows doesn't need special permissions.
    if (!isMacOs || !isDetectionOfUnscheduledMeetingsEnabled) return itemList;

    itemList.push(
      {
        kind: ConfigurationListItemKind.Divider,
        id: "divider-accessibility",
      },
      {
        kind: ConfigurationListItemKind.Permission,
        id: "accessibility",
        title: "Accessibility",
        description: "Mem relies on accessibility features to detect when you've joined a meeting",
        status: accessibilityPermissionStatus,
        onEnable: () =>
          handleEnablePermission({
            kind: RecallPermissionKind.Accessibility,
          }),
        onOpenSystemPreferences: () =>
          handleOpenPermissionPane({
            kind: RecallPermissionKind.Accessibility,
          }),
      }
    );

    if (advancedMeetingPermissionsRequired) {
      itemList.push({
        kind: ConfigurationListItemKind.Divider,
        id: "divider-full-disk-access",
      });

      itemList.push({
        kind: ConfigurationListItemKind.Permission,
        id: "full-disk-access",
        title: "Full disk access",
        description:
          "Mem relies on disk access functionality to improve detection of Microsoft Teams meetings",
        status: resolveConfigurationStatus({ kind: RecallPermissionKind.FullDiskAccess }),
        onEnable: () =>
          handleEnablePermission({
            kind: RecallPermissionKind.FullDiskAccess,
          }),
        onOpenSystemPreferences: () =>
          handleOpenPermissionPane({
            kind: RecallPermissionKind.FullDiskAccess,
          }),
      });
    }

    if (restartRequired) {
      itemList.push({
        kind: ConfigurationListItemKind.Divider,
        id: "divider-restart",
      });

      itemList.push({
        kind: ConfigurationListItemKind.Button,
        id: "restart",
        title: "Restart Mem",
        description: "To complete setup, restart Mem.",
        actionLabel: "Restart",
        variant: MDSButtonVariant.Contrast,
        onClick: onRestart,
      });
    }

    return itemList;
  }, [
    accessibilityPermissionStatus,
    handleEnablePermission,
    handleOpenPermissionPane,
    onRestart,
    resolveConfigurationStatus,
    restartRequired,
    advancedMeetingPermissionsRequired,
    isDetectionOfUnscheduledMeetingsEnabled,
    isIntelMacDesktop,
    isMacOs,
    onChangeDetectionOfUnscheduledMeetings,
  ]);

  const calendarEventItems = useMemo<ConfigurationListItem[]>(() => {
    const itemList: ConfigurationListItem[] = [
      {
        kind: ConfigurationListItemKind.Toggle,
        id: "remind-me-to-join-meetings-and-take-notes",
        title: "Remind me to join meetings and take notes as they begin",
        checked: isRemindMeToJoinMeetingsAndTakeNotesEnabled,
        onChange: onChangeRemindMeToJoinMeetingsAndTakeNotes,
      },
    ];

    return itemList;
  }, [isRemindMeToJoinMeetingsAndTakeNotesEnabled, onChangeRemindMeToJoinMeetingsAndTakeNotes]);

  return (
    <ModalDialog isOpen={isOpen} handleClose={onClose} isDismissable={false}>
      <Header slot={MDSDialogSlotName.Header}>
        <MDSText variant={MDSTextVariant.HeaderL} weight={MDSTextWeight.SemiBold}>
          Set up meeting notifications
        </MDSText>
      </Header>

      <DialogContent slot={MDSDialogSlotName.Content}>
        <Description variant={MDSTextVariant.ParagraphSm}>
          Mem will help you jump into meetings and take notes for you any time a meeting begins
        </Description>

        <SectionHeaderRow
          data={{
            type: ItemListRowType.SectionHeader,
            key: "calendar-events",
            payload: { title: "Calendar events" },
          }}
        />
        <ConfigurationList items={calendarEventItems} />

        {items.length > 0 && (
          <>
            <SectionHeaderRow
              data={{
                type: ItemListRowType.SectionHeader,
                key: "unscheduled-meetings",
                payload: { title: "Unscheduled meetings" },
              }}
            />
            <ConfigurationList items={items} />
          </>
        )}
      </DialogContent>

      <ButtonGroup slot={MDSDialogSlotName.ButtonsRight}>
        <MDSButton
          variant={MDSButtonVariant.Contrast}
          onClick={onClose}
          isDisabled={
            isDetectionOfUnscheduledMeetingsEnabled &&
            accessibilityPermissionStatus !== ConfigurationPermissionStatus.Granted
          }
        >
          Next
        </MDSButton>
      </ButtonGroup>
    </ModalDialog>
  );
};

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: ${({ theme }) => theme.spacing["2"]};
`;

const DialogContent = styled(MDSDialogTextContent)`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const Description = styled(MDSText)`
  padding: ${({ theme }) => theme.spacing["2"]} 0;
`;

const ModalDialog = styled(MDSModalDialog)`
  .mds-dialog {
    padding: ${({ theme }) => `${theme.spacing[7]}`};
  }
`;

const SectionHeaderRow = styled(ItemListSectionHeaderRow)`
  padding-bottom: 10px;
  padding-inline: 0;
  padding-top: 10px;
  margin-top: ${({ theme }) => theme.spacing["4"]};
`;

const ButtonGroup = styled(MDSButtonGroup)`
  padding-top: ${({ theme }) => theme.spacing["2"]};
`;
